// Settings reconcile worker.
//
// Triggers a sync attempt when:
//   - App boots and the device is online
//   - Network goes from offline → online
//   - The native shell flips from "bundled" → "remote"
//   - The app is foregrounded
//
// Behavior: per project policy, last-write-wins from THIS device. If the local
// cache is dirty, push it to Supabase. Otherwise pull the server copy down
// into the local mirror (so a brand-new device gets server state on first
// successful launch).
//
// No conflict prompt. No backoff queue — Supabase calls are idempotent upserts
// and the worker retries on the next trigger.

import { addConnectionListener } from "@/lib/native/network";
import { onShellModeChange } from "@/lib/native/shell-mode";
import {
  hydrateLocalUploadRules,
  getLocalUploadRulesSync,
  markUploadRulesSynced,
  mirrorServerUploadRules,
} from "./upload-rules-local";
import { hydrateCachedTrip, getCachedTripSync } from "./trip-cache";

let running = false;
let pending = false;

async function pushLocalToServer(tripId: string): Promise<void> {
  const local = getLocalUploadRulesSync(tripId);
  const { saveUploadRules } = await import("@/lib/media.functions");
  await saveUploadRules({
    data: {
      tripId,
      wifiOnly: local.wifiOnly,
      uploadOriginals: local.uploadOriginals,
      compressedPreviewsFirst: local.compressedPreviewsFirst,
      uploadMode: local.uploadMode,
      minBatteryPct: local.minBatteryPct,
      requireChargingUnderMin: local.requireChargingUnderMin,
    },
  });
  await markUploadRulesSynced(tripId);
  console.info("[settings-sync] pushed local upload rules to server", { tripId });
}

async function pullServerToLocal(tripId: string): Promise<void> {
  const { getUploadRules } = await import("@/lib/media.functions");
  const server = await getUploadRules({ data: { tripId } });
  if (!server) return;
  await mirrorServerUploadRules(
    {
      wifiOnly: server.wifiOnly,
      uploadOriginals: server.uploadOriginals,
      compressedPreviewsFirst: server.compressedPreviewsFirst,
      uploadMode: server.uploadMode,
      minBatteryPct: server.minBatteryPct,
      requireChargingUnderMin: server.requireChargingUnderMin,
    },
    tripId,
  );
}

export async function reconcileSettingsNow(): Promise<void> {
  if (running) {
    pending = true;
    return;
  }
  running = true;
  try {
    await Promise.all([hydrateLocalUploadRules(), hydrateCachedTrip()]);
    const trip = getCachedTripSync();
    const local = getLocalUploadRulesSync();
    const tripId = local.tripId ?? trip?.id ?? null;
    if (!tripId) {
      console.info("[settings-sync] no trip id available, skipping");
      return;
    }
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      console.info("[settings-sync] navigator offline, skipping");
      return;
    }
    if (local.dirty && local.tripId === tripId) {
      try {
        await pushLocalToServer(tripId);
      } catch (e) {
        console.warn("[settings-sync] push failed", e);
      }
    } else {
      try {
        await pullServerToLocal(tripId);
      } catch (e) {
        console.warn("[settings-sync] pull failed (likely offline)", e);
      }
    }
  } finally {
    running = false;
    if (pending) {
      pending = false;
      void reconcileSettingsNow();
    }
  }
}

let installed = false;

export function installSettingsSync(): void {
  if (installed) return;
  installed = true;

  // Best-effort initial reconcile shortly after boot. Delay so auth has a
  // chance to settle and getMyTrip can populate the trip cache.
  if (typeof window !== "undefined") {
    window.setTimeout(() => { void reconcileSettingsNow(); }, 5_000);
  }

  // Network up/down.
  addConnectionListener((t) => {
    if (t === "wifi" || t === "cellular") {
      void reconcileSettingsNow();
    }
  });

  // Shell mode flip → reconcile (remote means we likely have the network).
  onShellModeChange((snap) => {
    if (snap.mode === "remote") void reconcileSettingsNow();
  });

  // Foreground.
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") void reconcileSettingsNow();
    });
  }
}
